import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  output,
  viewChild,
} from '@angular/core';
import { IconsNgModule } from '@seech/icons-ng';
import { UxNgModule } from '@seech/ux-ng';
import {
  LayoutConversationViewModel,
  LayoutNavigationItemViewModel,
  LayoutProjectViewModel,
  LayoutSidebarCommand,
  LayoutSidebarViewModel,
} from './layout-presentation.models';

@Component({
  selector: 'app-layout-sidebar',
  standalone: true,
  imports: [IconsNgModule, UxNgModule],
  templateUrl: './layout-sidebar.component.html',
  styleUrl: './layout-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutSidebarComponent {
  readonly model = input.required<LayoutSidebarViewModel>();
  readonly collapsed = input(false);
  readonly mobileOpen = input(false);
  readonly commandRequested = output<LayoutSidebarCommand>();

  private readonly mobileCloseButton =
    viewChild<ElementRef<HTMLButtonElement>>('mobileCloseButton');

  focusMobileCloseButton(): void {
    this.mobileCloseButton()?.nativeElement.focus();
  }

  requestHome(event: MouseEvent): void {
    if (!isUnmodifiedPrimaryActivation(event)) {
      return;
    }

    event.preventDefault();
    this.commandRequested.emit(
      this.collapsed()
        ? { kind: 'expand-sidebar' }
        : { kind: 'navigate', route: '/' },
    );
  }

  requestNavigation(
    event: MouseEvent,
    item: LayoutNavigationItemViewModel,
  ): void {
    if (!isUnmodifiedPrimaryActivation(event)) {
      return;
    }

    event.preventDefault();
    this.commandRequested.emit({ kind: 'navigate', route: item.route });
  }

  requestProject(project: LayoutProjectViewModel): void {
    this.commandRequested.emit({
      kind: 'select-project',
      projectId: project.isSelected ? null : project.projectId,
    });
  }

  requestConversation(conversation: LayoutConversationViewModel): void {
    this.commandRequested.emit({
      kind: 'select-conversation',
      sessionId: conversation.sessionId,
    });
  }

  requestRename(conversation: LayoutConversationViewModel): void {
    this.commandRequested.emit({
      kind: 'rename-conversation',
      sessionId: conversation.sessionId,
      displayedTitle: conversation.title,
    });
  }

  requestArchive(conversation: LayoutConversationViewModel): void {
    this.commandRequested.emit({
      kind: 'archive-conversation',
      sessionId: conversation.sessionId,
    });
  }

  requestDelete(conversation: LayoutConversationViewModel): void {
    this.commandRequested.emit({
      kind: 'delete-conversation',
      sessionId: conversation.sessionId,
    });
  }

  requestSearch(): void {
    const sessionId = this.model().searchSessionId;
    if (sessionId) {
      this.commandRequested.emit({
        kind: 'search-conversations',
        sessionId,
      });
    }
  }
}

function isUnmodifiedPrimaryActivation(event: MouseEvent): boolean {
  return (
    event.button === 0 &&
    !event.altKey &&
    !event.ctrlKey &&
    !event.metaKey &&
    !event.shiftKey
  );
}
